import React, { useState } from 'react';
import { formatCurrency, round2 } from '../utils/checkoutCalculations';

const PRESETS = [10, 15, 20, 25];

/**
 * DiscountScreen
 * Clean whole-cart discount. Percentage presets or fixed amount.
 */
const DiscountScreen = ({ state, actions, onBack, currencySymbol }) => {
  const fmt = (amount) => formatCurrency(amount, currencySymbol);
  const subtotal = state.subtotal;

  const existing = state.cartDiscount;
  const [mode, setMode] = useState(existing?.type || 'percentage');
  const [percentValue, setPercentValue] = useState(existing?.type === 'percentage' ? existing.value : 0);
  const [fixedValue, setFixedValue] = useState(existing?.type === 'fixed' ? String(existing.value) : '');
  const [customActive, setCustomActive] = useState(existing?.type === 'percentage' && !PRESETS.includes(existing?.value));

  let discountAmount = 0;
  if (mode === 'percentage' && percentValue > 0) {
    discountAmount = round2(subtotal * (percentValue / 100));
  } else if (mode === 'fixed') {
    const parsed = parseFloat(fixedValue);
    if (!isNaN(parsed) && parsed > 0) {
      discountAmount = round2(Math.min(parsed, subtotal));
    }
  }
  const newTotal = round2(Math.max(0, subtotal - discountAmount));
  const hasDiscount = discountAmount > 0;

  const selectPreset = (p) => {
    setPercentValue(percentValue === p ? 0 : p);
    setCustomActive(false);
  };

  const handleApply = () => {
    if (mode === 'percentage' && percentValue > 0) {
      actions.setCartDiscount({ type: 'percentage', value: percentValue });
    } else if (mode === 'fixed' && discountAmount > 0) {
      actions.setCartDiscount({ type: 'fixed', value: parseFloat(fixedValue) });
    }
    onBack();
  };

  const handleRemove = () => {
    actions.clearDiscount();
    onBack();
  };

  return (
    <div className="co-discount">
      <div className="co-screen-header">
        <button className="co-back-btn" onClick={onBack}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <h2 className="co-screen-title">Discount</h2>
      </div>

      {/* Mode toggle */}
      <div className="co-discount-toggle">
        <button className={`co-discount-toggle-btn ${mode === 'percentage' ? 'co-discount-toggle-btn--active' : ''}`} onClick={() => { setMode('percentage'); }}>
          Percentage
        </button>
        <button className={`co-discount-toggle-btn ${mode === 'fixed' ? 'co-discount-toggle-btn--active' : ''}`} onClick={() => { setMode('fixed'); setPercentValue(0); setCustomActive(false); }}>
          Fixed Amount
        </button>
      </div>

      <div className="co-discount-body">
        {mode === 'percentage' ? (
          <>
            <div className="co-discount-presets">
              {PRESETS.map(p => (
                <button
                  key={p}
                  className={`co-discount-chip ${percentValue === p && !customActive ? 'co-discount-chip--active' : ''}`}
                  onClick={() => selectPreset(p)}
                >
                  {p}%
                </button>
              ))}
              <button
                className={`co-discount-chip ${customActive ? 'co-discount-chip--active' : ''}`}
                onClick={() => { setCustomActive(true); setPercentValue(0); }}
              >
                Custom
              </button>
            </div>
            {customActive && (
              <div className="co-discount-custom-row">
                <input
                  type="number"
                  className="co-discount-custom-input"
                  placeholder="0"
                  min="1"
                  max="100"
                  value={percentValue || ''}
                  onChange={e => {
                    const v = parseInt(e.target.value);
                    setPercentValue(isNaN(v) ? 0 : Math.min(100, Math.max(0, v)));
                  }}
                  autoFocus
                />
                <span className="co-discount-custom-unit">%</span>
              </div>
            )}
          </>
        ) : (
          <div className="co-discount-fixed-row">
            <span className="co-discount-fixed-symbol">{currencySymbol}</span>
            <input
              type="number"
              className="co-discount-fixed-input"
              placeholder="0.00"
              step="0.01"
              min="0"
              value={fixedValue}
              onChange={e => setFixedValue(e.target.value)}
              autoFocus
            />
          </div>
        )}

        {/* Live preview */}
        <div className="co-discount-preview">
          <div className="co-discount-preview-row">
            <span>Subtotal</span>
            <span>{fmt(subtotal)}</span>
          </div>
          {hasDiscount && (
            <div className="co-discount-preview-row co-discount-preview-row--savings">
              <span>Discount{mode === 'percentage' ? ` (${percentValue}%)` : ''}</span>
              <span>-{fmt(discountAmount)}</span>
            </div>
          )}
          <div className="co-discount-preview-row co-discount-preview-row--total">
            <span>New Total</span>
            <span>{fmt(newTotal)}</span>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="co-pl-footer">
        {existing ? (
          <>
            <button className="bdp-btn bdp-btn--secondary" style={{ color: '#ef4444' }} onClick={handleRemove}>
              Remove
            </button>
            <button className="bdp-btn bdp-btn--primary" onClick={handleApply} disabled={!hasDiscount}>
              Update Discount
            </button>
          </>
        ) : (
          <>
            <button className="bdp-btn bdp-btn--secondary" onClick={onBack}>
              Cancel
            </button>
            <button className="bdp-btn bdp-btn--primary" onClick={handleApply} disabled={!hasDiscount}>
              Apply Discount
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default DiscountScreen;
